import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { PopupForm } from "./components/PopupForm";
import { Home } from "./pages/Home";
import { About } from "./pages/About";
import { Contact } from "./pages/Contact";
import { Services } from "./pages/Services";
import { TrademarkRegistration } from "./pages/services/TrademarkRegistration";
import { CopyrightRegistration } from "./pages/services/CopyrightRegistration";
import { CompanyFormation } from "./pages/services/CompanyFormation";
import { TrademarkSearch } from "./pages/services/TrademarkSearch";
import { AmazonBrandRegistry } from "./pages/services/AmazonBrandRegistry";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { TermsOfService } from "./pages/TermsOfService";
import { LandingPage } from "./pages/LandingPage";
import { ApplicationMain } from "./pages/ApplicationMain";

// New Service Pages - Trademark Services
import { ClearanceSearch } from "./pages/services/ClearanceSearch";
import { TrademarkOwnershipTransfers } from "./pages/services/TrademarkOwnershipTransfers";
import { StatementsOfUse } from "./pages/services/StatementsOfUse";
import { TrademarkOfficeActions } from "./pages/services/TrademarkOfficeActions";
import { TrademarkMonitoring } from "./pages/services/TrademarkMonitoring";
import { TTABPetition } from "./pages/services/TTABPetition";

// Company Formation Services
import { LLCFormation } from "./pages/services/LLCFormation";
import { CCorporation } from "./pages/services/CCorporation";
import { SCorporation } from "./pages/services/SCorporation";
import { NonProfitFormation } from "./pages/services/NonProfitFormation";

// Copyright Services
import { CopyrightEnforcement } from "./pages/services/CopyrightEnforcement";
import { CopyrightTransfers } from "./pages/services/CopyrightTransfers";
import { CopyrightLegalRepresentation } from "./pages/services/CopyrightLegalRepresentation";
import { CopyrightPortfolio } from "./pages/services/CopyrightPortfolio";
import { DMCACompliance } from "./pages/services/DMCACompliance";

// Patent Services
import { PatentSearch } from "./pages/services/PatentSearch";
import { PatentOfficeActions } from "./pages/services/PatentOfficeActions";
import { PatentApplication } from "./pages/services/PatentApplication";
import { PatentLegalRepresentation } from "./pages/services/PatentLegalRepresentation";

// Legal Services
import { BusinessImmigration } from "./pages/services/BusinessImmigration";
import { DueDiligence } from "./pages/services/DueDiligence";
import { ITCommercialLaw } from "./pages/services/ITCommercialLaw";
import { PartnershipsLLPs } from "./pages/services/PartnershipsLLPs";
import { DataProtection } from "./pages/services/DataProtection";

// Additional Corporate Services
import { Bookkeeping } from "./pages/services/Bookkeeping";
import { BusinessFormation } from "./pages/services/BusinessFormation";
import { CertificateGoodStanding } from "./pages/services/CertificateGoodStanding";
import { CompanyDissolution } from "./pages/services/CompanyDissolution";
import { CompanyNameChange } from "./pages/services/CompanyNameChange";
import { CompanyRenewal } from "./pages/services/CompanyRenewal";
import { IssueShares } from "./pages/services/IssueShares";
import { NomineeDirector } from "./pages/services/NomineeDirector";
import { TaxAdvisory } from "./pages/services/TaxAdvisory";
import { TransferShares } from "./pages/services/TransferShares";
import { CFOServices } from "./pages/services/CFOServices";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <AppContent isPopupOpen={isPopupOpen} setIsPopupOpen={setIsPopupOpen} />
          <Toaster position="top-right" />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

function AppContent({ isPopupOpen, setIsPopupOpen }) {
  const location = useLocation();
  const isLandingPage = location.pathname === '/lp';

  return (
    <>
      <Header onOpenPopup={() => setIsPopupOpen(true)} isLandingPage={isLandingPage} />
      <PopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
      
      <Routes>
        <Route path="/" element={<Home onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/services" element={<Services />} />
        
        {/* Trademark Services */}
        <Route path="/services/trademark-registration" element={<TrademarkRegistration onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/trademark-search" element={<TrademarkSearch onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/clearance-search" element={<ClearanceSearch onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/trademark-ownership-transfers" element={<TrademarkOwnershipTransfers onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/statements-of-use" element={<StatementsOfUse onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/trademark-office-actions" element={<TrademarkOfficeActions onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/trademark-monitoring" element={<TrademarkMonitoring onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/ttab-petition" element={<TTABPetition onOpenPopup={() => setIsPopupOpen(true)} />} />
        
        {/* Company Formation Services */}
        <Route path="/services/company-formation" element={<CompanyFormation onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/llc-formation" element={<LLCFormation onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/c-corporation" element={<CCorporation onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/s-corporation" element={<SCorporation onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/non-profit-formation" element={<NonProfitFormation onOpenPopup={() => setIsPopupOpen(true)} />} />
        
        {/* Copyright Services */}
        <Route path="/services/copyright-registration" element={<CopyrightRegistration onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/copyright-enforcement" element={<CopyrightEnforcement onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/copyright-transfers" element={<CopyrightTransfers onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/copyright-legal-representation" element={<CopyrightLegalRepresentation onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/copyright-portfolio" element={<CopyrightPortfolio onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/dmca-compliance" element={<DMCACompliance onOpenPopup={() => setIsPopupOpen(true)} />} />
        
        {/* Patent Services */}
        <Route path="/services/patent-search" element={<PatentSearch onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/patent-office-actions" element={<PatentOfficeActions onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/patent-application" element={<PatentApplication onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/patent-legal-representation" element={<PatentLegalRepresentation onOpenPopup={() => setIsPopupOpen(true)} />} />
        
        {/* Legal Services */}
        <Route path="/services/business-immigration" element={<BusinessImmigration onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/due-diligence" element={<DueDiligence onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/it-commercial-law" element={<ITCommercialLaw onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/partnerships-llps" element={<PartnershipsLLPs onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/data-protection" element={<DataProtection onOpenPopup={() => setIsPopupOpen(true)} />} />
        
        {/* Additional Corporate Services */}
        <Route path="/services/amazon-brand-registry" element={<AmazonBrandRegistry onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/bookkeeping" element={<Bookkeeping onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/business-formation" element={<BusinessFormation onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/certificate-good-standing" element={<CertificateGoodStanding onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/company-dissolution" element={<CompanyDissolution onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/company-name-change" element={<CompanyNameChange onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/company-renewal" element={<CompanyRenewal onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/issue-shares" element={<IssueShares onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/nominee-director" element={<NomineeDirector onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/tax-advisory" element={<TaxAdvisory onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/transfer-shares" element={<TransferShares onOpenPopup={() => setIsPopupOpen(true)} />} />
        <Route path="/services/cfo-services" element={<CFOServices onOpenPopup={() => setIsPopupOpen(true)} />} />
        
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/lp" element={<LandingPage />} />
        <Route path="/application/main" element={<ApplicationMain />} />
      </Routes>
      
      {!isLandingPage && <Footer />}
    </>
  );
}

export default App;
